import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { NoteBlank, Copy, ArrowClockwise, DotsThreeVertical, PencilSimple, Trash, MonitorPlay, Sparkle, Info } from '@phosphor-icons/react';
import ConfirmDialog from '@/components/ConfirmDialog';
import { HERO } from '@/constants/testIds';

const HERO_STYLES = ['Celebrate', 'Gentle reminder', 'Heads up'];
const HERO_EXPIRES = [
    { value: '24h', label: '24 hours' },
    { value: '48h', label: '48 hours' },
    { value: '1w', label: '1 week' },
    { value: 'replaced', label: 'Until replaced' },
];
const HERO_ACCENT = {
    'Celebrate': { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-900' },
    'Gentle reminder': { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-900' },
    'Heads up': { bg: 'bg-sky-50', border: 'border-sky-300', text: 'text-sky-900' },
};

function HeroCardComposer({ facilityId, onSaved }) {
    const [open, setOpen] = useState(false);
    const [msg, setMsg] = useState('');
    const [style, setStyle] = useState('Celebrate');
    const [expires, setExpires] = useState('48h');
    const submit = async () => {
        if (!msg.trim()) { toast.error('Write a short message'); return; }
        try {
            await api.post(`/facilities/${facilityId}/hero-cards`, { message: msg, style, expires });
            toast.success('Posted to the board');
            setMsg(''); setOpen(false); onSaved?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid={HERO.manualAdd} className="rounded-full bg-primary text-primary-foreground">
                    <Sparkle size={14} className="mr-1" /> Add hero card
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading text-2xl">Post a hero card</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                        <Info size={14} className="mt-0.5 shrink-0" />
                        Anyone who can see this board can read this — keep it warm, keep details private.
                    </div>
                    <div>
                        <Label>Message</Label>
                        <Textarea data-testid={HERO.manualMessage} rows={3} value={msg} onChange={e => setMsg(e.target.value)}
                            placeholder="So proud of you both for doing your check-ins this week! 🎉 — J" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label>Style</Label>
                            <Select value={style} onValueChange={setStyle}>
                                <SelectTrigger data-testid={HERO.manualStyle}><SelectValue /></SelectTrigger>
                                <SelectContent>{HERO_STYLES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Expires</Label>
                            <Select value={expires} onValueChange={setExpires}>
                                <SelectTrigger data-testid={HERO.manualExpires}><SelectValue /></SelectTrigger>
                                <SelectContent>{HERO_EXPIRES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button data-testid={HERO.manualSubmit} onClick={submit} className="rounded-full bg-primary text-primary-foreground">Post to board</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default function HouseholdBoardTab({ facilityId }) {
    const qc = useQueryClient();
    const [text, setText] = useState('');
    const [edit, setEdit] = useState(null);
    const [del, setDel] = useState(null);

    const { data: tokenData } = useQuery({
        queryKey: ['board-token', facilityId],
        queryFn: async () => (await api.get(`/facilities/${facilityId}/board-token`)).data,
    });
    const { data: notes = [] } = useQuery({
        queryKey: ['board-notes', facilityId],
        queryFn: async () => (await api.get(`/facilities/${facilityId}/board-notes`)).data,
    });
    const { data: hero = { active: null, history: [] } } = useQuery({
        queryKey: ['hero-cards', facilityId],
        queryFn: async () => (await api.get(`/facilities/${facilityId}/hero-cards`)).data,
    });

    const boardUrl = tokenData?.board_token ? `${window.location.origin}/board/${tokenData.board_token}` : '';
    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['board-notes', facilityId] });
        qc.invalidateQueries({ queryKey: ['hero-cards', facilityId] });
    };

    const post = async () => {
        if (!text.trim()) return;
        try {
            await api.post(`/facilities/${facilityId}/board-notes`, { text });
            toast.success('Posted to the board');
            setText(''); refresh();
        } catch { toast.error('Failed'); }
    };
    const saveEdit = async () => {
        try {
            await api.patch(`/board-notes/${edit.board_note_id}`, { text: edit.text });
            toast.success('Saved');
            setEdit(null); refresh();
        } catch { toast.error('Failed'); }
    };
    const doDelete = async () => {
        try {
            await api.delete(`/board-notes/${del.board_note_id}`);
            toast.success('Removed'); setDel(null); refresh();
        } catch { toast.error('Failed'); }
    };
    const rotate = async () => {
        try {
            await api.post(`/facilities/${facilityId}/board-token/rotate`);
            toast.success('New link generated — old link no longer works');
            qc.invalidateQueries({ queryKey: ['board-token', facilityId] });
        } catch { toast.error('Failed'); }
    };
    const removeHero = async () => {
        try {
            await api.delete(`/hero-cards/${hero.active.hero_card_id}`);
            toast.success('Removed hero card'); refresh();
        } catch { toast.error('Failed'); }
    };

    const accent = HERO_ACCENT[hero.active?.style] || HERO_ACCENT['Celebrate'];

    const { data: facility } = useQuery({ queryKey: ['facility', facilityId], queryFn: async () => (await api.get(`/facilities/${facilityId}`)).data });
    const toggleMeds = async (v) => {
        try {
            await api.patch(`/facilities/${facilityId}`, { board_show_medications: v });
            qc.invalidateQueries({ queryKey: ['facility', facilityId] });
            toast.success(v ? 'Medication names & times will show on the board' : 'Medications hidden from the board');
        } catch { toast.error('Failed'); }
    };

    return (
        <div className="space-y-6">
            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-card p-4 shadow-sm cursor-pointer">
                <input data-testid="board-show-medications" type="checkbox" className="mt-1 h-5 w-5 accent-[#146B5E]" checked={!!facility?.board_show_medications} onChange={e => toggleMeds(e.target.checked)} />
                <span className="text-sm text-slate-700"><b>Show the medication schedule on the board</b> — medication names and times of day only. Whether something was taken or skipped is never shown on the board.</span>
            </label>
            {/* Hero card composer */}
            <div className={`rounded-xl border-2 ${hero.active ? accent.border : 'border-slate-200'} ${hero.active ? accent.bg : 'bg-card'} p-5 shadow-sm`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                        <Sparkle size={18} weight="duotone" className="text-primary" />
                        <div className="font-heading text-xl text-slate-900">Hero card</div>
                        <span className="text-[11px] text-slate-500">The bright pinned message on the board</span>
                    </div>
                    <HeroCardComposer facilityId={facilityId} onSaved={refresh} />
                </div>
                {hero.active ? (
                    <div data-testid={HERO.banner} className="mt-2">
                        <div className={`text-[10px] uppercase tracking-widest font-semibold ${accent.text}`}>{hero.active.style} · expires {hero.active.expires === 'replaced' ? 'when replaced' : hero.active.expires}</div>
                        <div className="text-lg text-slate-900 mt-1 whitespace-pre-wrap">{hero.active.message}</div>
                        <div className="flex items-center gap-3 mt-2">
                            <div className="text-[11px] text-slate-600">{hero.active.created_by_name || 'Coordinator'} · {new Date(hero.active.created_at).toLocaleString()}</div>
                            <button data-testid={HERO.manualDelete} onClick={removeHero} className="text-[11px] text-red-600 hover:underline">Remove</button>
                        </div>
                    </div>
                ) : (
                    <div className="text-sm text-slate-600">Nothing pinned right now. Post a note of encouragement, a friendly reminder, or a heads-up.</div>
                )}
                {hero.history.length > 0 && (
                    <details className="mt-4 group">
                        <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-900">History ({hero.history.length})</summary>
                        <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                            {hero.history.map(h => (
                                <div key={h.hero_card_id} data-testid={HERO.history} className="rounded-lg border border-slate-200 bg-white/80 px-3 py-2">
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{h.style} · {h.status} · {new Date(h.created_at).toLocaleDateString()}</div>
                                    <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{h.message}</div>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </div>

            {/* Share the board */}
            <div className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
                <div className="flex items-start gap-3">
                    <MonitorPlay size={22} className="text-primary mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-heading text-xl text-slate-900">Fridge board</div>
                        <p className="text-sm text-slate-600 mt-1">
                            Open this link on the tablet or old phone you keep on the fridge. Big type, auto-refreshes, no medical details — just the appointments and notes your loved one needs to see.
                        </p>
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                    <Input readOnly value={boardUrl} data-testid="board-share-url" className="font-mono text-xs" />
                    <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(boardUrl); toast.success('Copied'); }} className="shrink-0">
                        <Copy size={16} />
                    </Button>
                    <Button variant="outline" data-testid="board-open" onClick={() => window.open(boardUrl, '_blank', 'noopener')} className="shrink-0 rounded-full">
                        Open
                    </Button>
                    <Button variant="outline" data-testid="board-rotate" onClick={rotate} className="shrink-0 rounded-full text-slate-600">
                        <ArrowClockwise size={14} className="mr-1" /> New link
                    </Button>
                </div>
            </div>

            {/* Post a note */}
            <div className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
                <div className="font-heading text-xl text-slate-900 flex items-center gap-2"><NoteBlank size={20} weight="duotone" /> Whiteboard notes</div>
                <p className="text-sm text-slate-600 mt-1">Little messages that show on the fridge board — reminders, love notes, driving arrangements.</p>
                <div className="mt-3 flex items-start gap-2">
                    <Textarea
                        data-testid="board-note-input"
                        rows={2}
                        placeholder='e.g. "Ma — I&apos;ll be by Saturday morning. Love you both."'
                        value={text} onChange={e => setText(e.target.value)}
                    />
                    <Button data-testid="board-note-submit" onClick={post} className="rounded-full bg-primary text-primary-foreground shrink-0">Post</Button>
                </div>
                <div className="mt-4 divide-y divide-slate-100">
                    {notes.length === 0 && <div className="py-6 text-center text-sm text-slate-500">No notes yet.</div>}
                    {notes.map(n => (
                        <div key={n.board_note_id} data-testid="board-note-row" className="py-3 flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="text-slate-800 whitespace-pre-wrap">{n.text}</div>
                                <div className="text-[11px] text-slate-500 mt-1">{n.author_name} · {new Date(n.created_at).toLocaleString()}</div>
                            </div>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="p-1 rounded hover:bg-slate-100 text-slate-500"><DotsThreeVertical size={16} weight="bold" /></button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem data-testid="board-note-edit" onClick={() => setEdit({ ...n })}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                    <DropdownMenuItem data-testid="board-note-delete" onClick={() => setDel(n)} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    ))}
                </div>
            </div>

            <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit board note</DialogTitle></DialogHeader>
                    <Textarea rows={3} value={edit?.text || ''} onChange={e => setEdit({ ...edit, text: e.target.value })} />
                    <DialogFooter><Button onClick={saveEdit} className="rounded-full bg-primary text-primary-foreground">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!del} onOpenChange={(v) => !v && setDel(null)}
                title="Remove this board note?"
                description="It will disappear from the fridge board immediately. This can't be undone."
                onConfirm={doDelete}
            />
        </div>
    );
}
